import { useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { PackageStatusCards } from './PackageStatusCards';
import { StuckPackagesAlert } from './StuckPackagesAlert';
import { InventoryHealthStrip } from './InventoryHealthStrip';

/**
 * DashboardOverview — Layer 1: The command center landing view.
 *
 * Fetches stats and low-stock inventory on mount.
 * Assembles the three overview panels in a responsive layout.
 */
export function DashboardOverview({
  dashboardData,
  onStatusCardClick,
  onInventoryClick,
  onPackageHistoryClick,
}) {
  const {
    stats, statsLoading, statsError, fetchStats,
    inventory, inventorySummary, inventoryLoading, fetchInventory,
  } = dashboardData;

  // Fetch both data domains on mount
  useEffect(() => {
    fetchStats();
    fetchInventory(true); // low stock only for the strip
  }, [fetchStats, fetchInventory]);

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">

        {/* ── Page heading ──────────────────────────────────────────────── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-montserrat font-extrabold text-2xl text-gray-800">
              Centro de Control
            </h1>
            <p className="font-montserrat text-sm text-gray-500 mt-0.5">
              Vista general de paquetes e inventario
            </p>
          </div>
          <button
            onClick={() => { fetchStats(); fetchInventory(true); }}
            disabled={statsLoading || inventoryLoading}
            className="flex items-center gap-2 text-sm font-montserrat font-semibold text-gray-500 hover:text-brand-primary transition-colors disabled:opacity-50"
          >
            <RefreshCw
              size={15}
              className={statsLoading || inventoryLoading ? 'animate-spin' : ''}
            />
            Actualizar
          </button>
        </div>

        {/* ── Error state ────────────────────────────────────────────────── */}
        {statsError && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm font-montserrat text-red-600">
            {statsError}
          </div>
        )}

        {/* ── Status KPI cards ───────────────────────────────────────────── */}
        <PackageStatusCards
          counts={stats?.counts}
          total={stats?.total}
          loading={statsLoading}
          onStatusClick={onStatusCardClick}
        />

        {/* ── Two-column layout: stuck alerts + inventory health ─────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <StuckPackagesAlert
            stuckPackages={stats?.stuck_packages}
            threshold={stats?.stuck_threshold_hours}
            loading={statsLoading}
            onPackageClick={onPackageHistoryClick}
          />

          <InventoryHealthStrip
            inventorySummary={inventorySummary}
            inventory={inventory}
            loading={inventoryLoading}
            onInventoryClick={onInventoryClick}
          />
        </div>

      </div>
    </div>
  );
}